import { Controller } from "@hotwired/stimulus";

// LOC-056: fills street, city and country on the Locals location step from a
// Dutch postcode and house number, via PDOK Locatieserver (called from the
// browser, no key). The lookup only assists: every field stays editable, a
// miss, a timeout or a network error leaves the fields alone and shows a hint.
const ENDPOINT = "https://api.pdok.nl/bzk/locatieserver/search/v3_1/free";
const TIMEOUT_MS = 3000;
const POSTCODE = /^\d{4}\s?[A-Za-z]{2}$/;
const HOUSE_NUMBER = /^\d+/;
const NETHERLANDS = ["", "nl", "nederland", "netherlands", "the netherlands", "pays-bas"];

export default class extends Controller {
  static targets = ["postcode", "houseNumber", "address", "city", "country", "hint"];
  static values = { country: String, notFound: String };

  lookup(event) {
    if (event) event.preventDefault();
    this.run();
  }

  blur() {
    this.run();
  }

  // LOC-068: Enter in the postcode or house number field runs the lookup
  // instead of the browser's implicit submit, which would post the step
  // through the first submit button in the form (Back) and leave the step.
  keydown(event) {
    if (event.key !== "Enter") return;

    event.preventDefault();
    this.run();
  }

  async run() {
    const postcode = this.postcodeTarget.value.trim();
    const houseNumber = this.houseNumberTarget.value.trim();

    this.hintTarget.textContent = "";
    if (!this.dutch() || !POSTCODE.test(postcode) || !HOUSE_NUMBER.test(houseNumber)) return;

    const found = await this.fetchAddress(postcode.replace(/\s/g, "").toUpperCase(), houseNumber.match(HOUSE_NUMBER)[0]);
    if (!found) {
      this.hintTarget.textContent = this.notFoundValue;
      return;
    }

    this.addressTarget.value = `${found.straatnaam} ${houseNumber}`;
    this.cityTarget.value = found.woonplaatsnaam;
    this.countryTarget.value = this.countryValue;
  }

  dutch() {
    return NETHERLANDS.includes(this.countryTarget.value.trim().toLowerCase());
  }

  async fetchAddress(postcode, number) {
    const params = new URLSearchParams({
      q: `postcode:${postcode} and huisnummer:${number}`,
      fq: "type:adres",
      rows: "1",
    });
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
      const response = await fetch(`${ENDPOINT}?${params}`, { signal: controller.signal });
      if (!response.ok) return null;

      const doc = (await response.json()).response.docs[0];
      return doc && doc.straatnaam && doc.woonplaatsnaam ? doc : null;
    } catch (_error) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }
}
